import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Box, Spinner, Text, Stack } from "@chakra-ui/react";
import { useCmsAdminApi } from "../../services/useCmsAdminApi";

// Note: sessionStorage is used (tab-scoped). For enhanced security, consider httpOnly cookies with BFF pattern.
export const CmsCallbackPage = (): JSX.Element => {
	const navigate = useNavigate();
	const adminApi = useCmsAdminApi();
	const hasRun = useRef(false);

	useEffect(() => {
		if (hasRun.current) return;
		hasRun.current = true;

		const hash = window.location.hash.substring(1);
		const params = new URLSearchParams(hash);

		// Validate OAuth state parameter (CSRF protection)
		const state = params.get("state");
		const expectedState = sessionStorage.getItem("oauth_state");
		if (!state || state !== expectedState) {
			navigate("/cms/login", { replace: true });
			return;
		}
		sessionStorage.removeItem("oauth_state");

		const idToken = params.get("id_token");

		if (!idToken) {
			navigate("/cms/login", { replace: true });
			return;
		}

		// Decode JWT payload (base64url → base64) to check nonce
		try {
			const raw = idToken.split('.')[1];
			const b64 = raw.replace(/-/g, '+').replace(/_/g, '/');
			const payload = JSON.parse(atob(b64));
			const expectedNonce = sessionStorage.getItem("oauth_nonce");
			if (!payload.nonce || payload.nonce !== expectedNonce) {
				alert("Invalid token nonce. Please try logging in again.");
				navigate("/cms/login", { replace: true });
				return;
			}
			sessionStorage.removeItem("oauth_nonce");
		} catch {
			navigate("/cms/login", { replace: true });
			return;
		}

		// Validate token against backend whitelist
		adminApi.validate(idToken).then((result) => {
			if (result?.authorized) {
				sessionStorage.setItem("cmsToken", idToken);
				sessionStorage.setItem("cmsEmail", result.email);
				navigate("/cms", { replace: true });
			} else {
				alert("Your email is not authorized to access the CMS.");
				navigate("/cms/login", { replace: true });
			}
		}).catch(() => {
			navigate("/cms/login", { replace: true });
		});
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [navigate]);

	return (
		<Box minH="100vh" display="flex" alignItems="center" justifyContent="center">
			<Stack spacing="4" textAlign="center">
				<Spinner size="xl" color="green.500" mx="auto" />
				<Text color="gray.600">Validating your access...</Text>
			</Stack>
		</Box>
	);
};
